"use client";

import { Link } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Send, Upload, X } from "lucide-react";
import { type FormEvent, useEffect, useId, useState } from "react";
import ReactMarkdown from "react-markdown";
import { FadeIn } from "@/components/ui/motion";
import { useLang, useLangPrefix } from "@/contexts/lang";
import type { SectionIds } from "@/data/home";
import type { JobPosting } from "@/lib/jobPostings";
import { fileToBase64, submitApplication } from "@/lib/submitApplication";

type CareerSectionProps = {
	sectionIds: SectionIds;
	positions: JobPosting[];
};

export function CareerSection({ sectionIds, positions }: CareerSectionProps) {
	const [applying, setApplying] = useState<JobPosting | null>(null);
	const t = useLang();
	const prefix = useLangPrefix();

	return (
		<section
			id={sectionIds.career}
			className="scroll-mt-32 bg-primary py-16 sm:py-20 md:py-28 mt-grain"
		>
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				{/* Header */}
				<FadeIn>
					<div className="mb-10 sm:mb-12 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
						<div>
							<div className="flex items-center gap-4 mb-4">
								<span className="h-px w-10 sm:w-16 bg-accent/60" />
								<p className="text-label text-accent">
									{t.careerSection.label}
								</p>
							</div>
							<h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-normal text-white leading-[1.1] tracking-tight mb-4 sm:mb-5">
								{t.careerSection.heading1}
								<span className="block mt-1 font-semibold text-white/50">
									{t.careerSection.heading2}
								</span>
							</h2>
							<p className="text-sm sm:text-base text-white/55 leading-relaxed max-w-lg">
								{t.careerSection.description}
							</p>
						</div>

						<div className="flex items-baseline gap-2 shrink-0 sm:flex-col sm:items-end sm:gap-0.5">
							<span className="font-display text-5xl sm:text-6xl font-light leading-none tabular-nums select-none text-accent/10">
								{String(positions.length).padStart(2, "0")}
							</span>
							<span className="text-xs font-bold uppercase tracking-[0.16em] text-accent/30">
								{t.careerSection.openPositions}
							</span>
						</div>
					</div>
				</FadeIn>

				{/* Table */}
				<FadeIn delay={0.08}>
					<div className="rounded-sm border border-white/[0.08] overflow-hidden">
						{/* Column headers */}
						<div className="hidden sm:grid sm:grid-cols-[1fr_110px_120px] items-center gap-4 px-6 py-2.5 border-b border-white/[0.07] bg-white/[0.02]">
							<span className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/45">
								{t.careerSection.positionCol}
							</span>
							<span className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/45">
								{t.careerSection.typeCol}
							</span>
							<span />
						</div>

						{/* Rows */}
						{positions.length > 0 ? (
							positions
								.slice(0, 5)
								.map((position, idx) => (
									<PositionRow
										key={position.documentId}
										position={position}
										index={idx}
										isLast={idx === Math.min(positions.length, 5) - 1}
										onApply={() => setApplying(position)}
									/>
								))
						) : (
							<div className="px-6 py-12 text-center">
								<p className="text-sm text-white/35 italic">
									{t.careerSection.noPositions}
								</p>
							</div>
						)}
					</div>
				</FadeIn>

				{/* Footer */}
				<div className="mt-6 flex justify-end">
					<Link
						to={prefix === "" ? "/career" : "/en/career"}
						className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-white/45 transition-colors duration-300 hover:text-accent"
					>
						{t.careerSection.seeAll}
						<ArrowRight className="h-3.5 w-3.5" />
					</Link>
				</div>
			</div>

			<AnimatePresence>
				{applying ? (
					<ApplyModal position={applying} onClose={() => setApplying(null)} />
				) : null}
			</AnimatePresence>
		</section>
	);
}

// ─── Position row ─────────────────────────────────────────────────────────────

type RowProps = {
	position: JobPosting;
	index: number;
	isLast: boolean;
	onApply: () => void;
};

function PositionRow({ position, index, isLast, onApply }: RowProps) {
	const t = useLang();
	const isPartTime = position.type.toLowerCase().includes("скратено");

	return (
		<button
			type="button"
			onClick={onApply}
			className={`group relative flex flex-col w-full text-left sm:grid sm:grid-cols-[1fr_110px_120px] sm:items-center gap-2 sm:gap-4 px-5 sm:px-6 py-4 sm:py-3.5 transition-colors duration-200 hover:bg-white/[0.03] cursor-pointer ${
				!isLast ? "border-b border-white/[0.07]" : ""
			}`}
		>
			{/* Gold left accent */}
			<div className="absolute inset-y-0 left-0 w-[2px] origin-center scale-y-0 bg-accent transition-transform duration-300 group-hover:scale-y-100" />

			{/* Title + location */}
			<div className="min-w-0">
				<div className="flex items-baseline gap-2.5">
					<span className="shrink-0 font-display text-[11px] tabular-nums text-white/[0.15] select-none">
						{String(index + 1).padStart(2, "0")}
					</span>
					<h3 className="font-display text-base sm:text-lg font-normal text-white leading-snug tracking-tight truncate">
						{position.title}
					</h3>
				</div>
				<p className="mt-1 ml-[28px] text-[11px] leading-snug text-white/65 line-clamp-1 group-hover:line-clamp-2 transition-all duration-200">
					{position.location}
				</p>
			</div>

			{/* Type badge */}
			<div className="ml-[28px] sm:ml-0">
				<span
					className={`inline-flex rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.14em] ${
						isPartTime
							? "bg-white/[0.06] text-white/55"
							: "bg-accent/[0.09] text-accent/55"
					}`}
				>
					{isPartTime ? t.careerSection.partTime : t.careerSection.fullTime}
				</span>
			</div>

			{/* Apply */}
			<div className="ml-[28px] sm:ml-0 sm:flex sm:justify-end">
				<span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white/55 transition-all duration-300 group-hover:text-accent">
					{t.careerSection.apply}
					<ArrowRight className="h-2.5 w-2.5 transition-transform duration-300 group-hover:translate-x-0.5" />
				</span>
			</div>
		</button>
	);
}

// ─── Apply modal ──────────────────────────────────────────────────────────────

type ModalProps = {
	position: JobPosting;
	onClose: () => void;
};

function ApplyModal({ position, onClose }: ModalProps) {
	const formId = useId();
	const t = useLang();
	const isPartTime = position.type.toLowerCase().includes("скратено");
	const [file, setFile] = useState<File | null>(null);
	const [status, setStatus] = useState<
		"idle" | "loading" | "success" | "error"
	>("idle");

	async function handleSubmit(e: FormEvent<HTMLFormElement>) {
		e.preventDefault();
		if (!file || status === "loading") return;
		const fd = new FormData(e.currentTarget);
		const name = (fd.get("name") as string).trim();
		const email = (fd.get("email") as string).trim();
		const phone = (fd.get("phone") as string | null)?.trim() || undefined;
		setStatus("loading");
		try {
			const cvBase64 = await fileToBase64(file);
			await submitApplication({
				data: {
					name,
					email,
					phone,
					cvBase64,
					cvName: file.name,
					jobPostingDocumentId: position.documentId,
				},
			});
			setStatus("success");
		} catch {
			setStatus("error");
		}
	}

	useEffect(() => {
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "";
		};
	}, []);

	useEffect(() => {
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") onClose();
		}
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [onClose]);

	return (
		<div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4">
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				transition={{ duration: 0.2 }}
				className="absolute inset-0 bg-primary/70 backdrop-blur-md"
				onClick={onClose}
				role="presentation"
			/>
			<motion.div
				initial={{ opacity: 0, y: 48 }}
				animate={{ opacity: 1, y: 0 }}
				exit={{ opacity: 0, y: 32 }}
				transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
				className="relative w-full sm:max-w-5xl bg-card rounded-t-sm sm:rounded-sm shadow-luxury-xl sm:ring-2 sm:ring-white/20 overflow-hidden max-h-[90dvh] flex flex-col"
			>
				{/* Gold top accent */}
				<div className="h-[2px] w-full bg-accent shrink-0" />

				{/* Close button */}
				<button
					type="button"
					onClick={onClose}
					className="absolute top-4 right-4 z-10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white/60 transition-all hover:bg-white/20 hover:text-white sm:bg-secondary sm:text-muted-foreground sm:hover:bg-secondary sm:hover:text-primary"
				>
					<X className="h-4 w-4" />
				</button>

				<div className="flex flex-col sm:flex-row flex-1 min-h-0">
					{/* Left: Scrollable job details panel */}
					<div className="bg-primary px-6 py-6 sm:w-[52%] sm:shrink-0 sm:px-8 sm:py-8 overflow-y-auto">
						<p className="text-[10px] font-bold uppercase tracking-[0.18em] text-accent mb-3">
							{t.careerSection.modalPositionLabel}
						</p>
						<h3 className="font-display text-xl sm:text-2xl font-normal text-white leading-snug mb-3">
							{position.title}
						</h3>
						<span
							className={`inline-flex rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.14em] mb-5 ${
								isPartTime
									? "bg-white/10 text-white/60"
									: "bg-accent/15 text-accent/70"
							}`}
						>
							{isPartTime
								? t.careerSection.modalPartTime
								: t.careerSection.modalFullTime}
						</span>

						{position.description && (
							<div className="prose prose-slate prose-invert prose-sm max-w-none">
								<ReactMarkdown>{position.description}</ReactMarkdown>
							</div>
						)}
					</div>

					{/* Right: Form */}
					<div className="flex-1 border-t border-white/10 sm:border-t-0 sm:border-l sm:border-border flex flex-col">
						<div className="px-6 py-5 sm:px-7 sm:py-6 border-b border-border shrink-0">
							<p className="text-[10px] font-bold uppercase tracking-[0.18em] text-accent mb-1">
								{t.careerSection.modalApplyLabel}
							</p>
							<p className="text-sm text-muted-foreground">
								{t.careerSection.modalFill}
							</p>
						</div>
						{status === "success" ? (
							<div className="flex flex-col items-center justify-center flex-1 py-12 text-center gap-4 px-6">
								<div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/10">
									<Send className="h-5 w-5 text-accent" />
								</div>
								<p className="font-display text-lg text-primary">
									{t.careerSection.successTitle}
								</p>
								<p className="text-sm text-muted-foreground max-w-xs">
									{t.careerSection.successDesc}
								</p>
							</div>
						) : (
							<form
								id={formId}
								onSubmit={handleSubmit}
								className="px-6 py-5 sm:px-7 sm:py-6 space-y-3 overflow-y-auto"
							>
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
									<input
										type="text"
										name="name"
										required
										placeholder={t.careerSection.namePlaceholder}
										className="sm:col-span-2 w-full rounded-sm border border-border bg-background px-4 py-3 text-sm text-primary placeholder:text-muted-foreground focus:border-accent/50 focus:outline-none focus:ring-1 focus:ring-accent/30 transition-all"
									/>
									<input
										type="email"
										name="email"
										required
										placeholder="Email"
										className="w-full rounded-sm border border-border bg-background px-4 py-3 text-sm text-primary placeholder:text-muted-foreground focus:border-accent/50 focus:outline-none focus:ring-1 focus:ring-accent/30 transition-all"
									/>
									<input
										type="tel"
										name="phone"
										placeholder={t.careerSection.phonePlaceholder}
										className="w-full rounded-sm border border-border bg-background px-4 py-3 text-sm text-primary placeholder:text-muted-foreground focus:border-accent/50 focus:outline-none focus:ring-1 focus:ring-accent/30 transition-all"
									/>
								</div>
								<label className="flex cursor-pointer items-center gap-4 rounded-sm border border-dashed border-primary/12 bg-secondary/40 px-5 py-4 transition-all duration-300 hover:border-accent/40 hover:bg-secondary/60">
									<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-background shadow-luxury-sm">
										<Upload className="h-4 w-4 text-muted-foreground" />
									</div>
									<div className="flex-1 min-w-0">
										<span className="block text-sm font-medium text-primary/70 truncate">
											{file ? file.name : t.careerSection.uploadResume}
										</span>
										<span className="text-[10px] text-muted-foreground">
											{t.careerSection.uploadDesc}
										</span>
									</div>
									<input
										type="file"
										name="cv"
										accept=".pdf"
										required
										onChange={(e) => setFile(e.target.files?.[0] ?? null)}
										className="sr-only"
									/>
								</label>
								<button
									type="submit"
									disabled={status === "loading"}
									className="w-full inline-flex cursor-pointer items-center justify-center gap-2 rounded-sm bg-accent py-3.5 text-sm font-semibold text-primary shadow-gold transition-all duration-300 hover:bg-accent-light hover:shadow-gold-lg disabled:opacity-60 disabled:cursor-not-allowed"
								>
									<span>
										{status === "loading"
											? t.careerSection.submitting
											: t.careerSection.submit}
									</span>
									<Send className="h-3.5 w-3.5" />
								</button>
								{status === "error" && (
									<p className="text-center text-xs text-destructive">
										{t.careerSection.submitError}
									</p>
								)}
								<p className="text-center text-[10px] text-muted-foreground">
									{t.careerSection.privacy}
								</p>
							</form>
						)}
					</div>
				</div>
			</motion.div>
		</div>
	);
}
